import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Tabs } from 'antd'
import { SkeletonSurveyInsights } from '../../../features/skeleton'
import { IndividualResponsesView } from '../components/insights'
import { SurveySynthesisTab } from '../components/insights/SurveySynthesisTab'
import { AllResponsesTab } from '../components/insights/AllResponsesTab'
import { ValidationScopeTab } from '../../../components/common/ValidationScopeTab'
import { useSurveyCompletion } from '../hooks/useSurveyCompletion'
import { useSurveyInsightsActions } from '../hooks/useSurveyInsightsActions'
import { useSurveyResponsesData } from '../hooks/useSurveyResponsesData'
import { SurveyInsightsHeader } from './SurveyInsightsHeader'
import { SurveyInsightsMeta } from './SurveyInsightsMeta'
import { SurveyInsightsBanners } from './SurveyInsightsBanners'
import { InsightsContainer, Divider } from './SurveyInsightsPageStyles'
import { PRINT_STYLES } from '../../../styles/pageHeaderStyles'
import styled from 'styled-components'
import { SPACING } from '../../../styles/theme'
import { reconcileQuestions } from './surveyInsightsUtils'
export type { ReconciledQuestion } from './surveyInsightsUtils'

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${SPACING.xxxl} ${SPACING.xl};
  color: var(--color-text-secondary);
`

export function SurveyInsightsPage() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const [isPrinting, setIsPrinting] = useState(false)

  const { data, loading, error, refetch } = useSurveyResponsesData(nodeId)

  const { complete, reopen, isCompleting, isReopening } = useSurveyCompletion({
    mindmapId: data?.mindmapId,
    surveyId: nodeId,
    onUpdated: () => void refetch(),
  })

  const { handleSimulate, handleDelete, shareMenuItems, simulating } = useSurveyInsightsActions({
    nodeId,
    mindmapId: data?.mindmapId,
    parentLabel: data?.parentLabel,
    onResponsesChanged: () => void refetch(),
  })

  useEffect(() => {
    const mq = window.matchMedia('print')
    const handler = (e: MediaQueryListEvent) => setIsPrinting(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const reconciledQuestions = useMemo(
    () => reconcileQuestions(data?.questions ?? [], data?.responses ?? []),
    [data]
  )

  if (loading) return <SkeletonSurveyInsights />

  if (error || !data) {
    return (
      <InsightsContainer>
        <ErrorContainer>{error ?? 'Something went wrong.'}</ErrorContainer>
      </InsightsContainer>
    )
  }

  const {
    parentLabel,
    questions,
    responses,
    status,
    aiSummaryStructured,
    aiSummaryGeneratedAt,
    aiSummaryResponseCount,
  } = data

  const isClosed = status === 'closed'
  const hasSummary = aiSummaryStructured != null
  const isStale =
    !isClosed &&
    hasSummary &&
    aiSummaryResponseCount != null &&
    aiSummaryResponseCount !== responses.length
  const newResponsesSinceSynth =
    aiSummaryResponseCount != null ? responses.length - aiSummaryResponseCount : 0

  // Legacy responses (no snapshot) with a different answer count than the current questions
  const hasMismatch = responses.some(
    (r) => r.questionsSnapshot === null && r.answers.length !== questions.length
  )

  return (
    <InsightsContainer>
      <style>{PRINT_STYLES}</style>

      <SurveyInsightsHeader
        parentLabel={parentLabel}
        questionCount={questions.length}
        isClosed={isClosed}
        hasResponses={responses.length > 0}
        isCompleting={isCompleting}
        isReopening={isReopening}
        simulating={simulating}
        mindmapId={data.mindmapId}
        nodeId={nodeId ?? ''}
        shareMenuItems={shareMenuItems}
        onComplete={complete}
        onReopen={() => void reopen()}
        onSimulate={() => void handleSimulate()}
        onDelete={handleDelete}
      />

      <SurveyInsightsMeta isClosed={isClosed} createdAt={data.createdAt} responses={responses} />

      <Divider />

      <SurveyInsightsBanners
        hasMismatch={hasMismatch}
        isStale={isStale}
        aiSummaryResponseCount={aiSummaryResponseCount}
        newResponsesSinceSynth={newResponsesSinceSynth}
      />

      <Tabs
        defaultActiveKey="synthesis"
        items={[
          {
            key: 'synthesis',
            label: 'Findings',
            children: (
              <SurveySynthesisTab
                status={status}
                responseCount={responses.length}
                structured={aiSummaryStructured}
                generatedAt={aiSummaryGeneratedAt}
                aiSummaryResponseCount={aiSummaryResponseCount}
                mindmapId={data.mindmapId}
                shareMenuItems={shareMenuItems}
                onSimulate={() => void handleSimulate()}
                simulating={simulating}
                onComplete={complete}
                isCompleting={isCompleting}
              />
            ),
          },
          {
            key: 'all',
            label: 'All responses',
            children: (
              <AllResponsesTab
                reconciledQuestions={reconciledQuestions}
                responseCount={responses.length}
                isPrinting={isPrinting}
              />
            ),
          },
          {
            key: 'individual',
            label: 'Individual responses',
            children: (
              <IndividualResponsesView
                responses={responses}
                reconciledQuestions={reconciledQuestions}
              />
            ),
          },
          {
            key: 'scope',
            label: 'Mindmap nodes',
            children: (
              <ValidationScopeTab
                questions={questions}
                mindmapId={data.mindmapId}
                validationKind="survey"
              />
            ),
          },
        ]}
      />
    </InsightsContainer>
  )
}
